import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import {
  authApi,
  authKeys,
  browserTimezone,
  useAuthState,
  type AuthState,
  type TotpEnrollment,
} from '@/api/auth'
import { setActiveIdentity } from '@/api/identity'
import { Button, ErrorText, Input, PageSpinner } from '@/components/ui'

import { AuthLayout, RecoveryCodesList, TotpCodeInput, TotpQr } from './parts'
import { digitsOnly } from './utils'

const MIN_PASSWORD = 8

type Step =
  | { kind: 'password' }
  | { kind: 'totp'; enrollment: TotpEnrollment }
  | { kind: 'codes'; codes: string[] }

function StepMark({ current }: { current: 1 | 2 | 3 }) {
  return (
    <p className="mb-5 text-xs font-medium uppercase tracking-wide text-muted">
      Шаг {current} из 3
    </p>
  )
}

export function SetupPage() {
  const state = useAuthState()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const timezone = browserTimezone()

  const [step, setStep] = useState<Step>({ kind: 'password' })
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [repeat, setRepeat] = useState('')
  const [touched, setTouched] = useState(false)
  const [code, setCode] = useState('')
  const [saved, setSaved] = useState(false)

  const begin = useMutation({
    mutationFn: () => authApi.setup(email, password, timezone),
    onSuccess: (enrollment) => setStep({ kind: 'totp', enrollment }),
  })

  const confirm = useMutation({
    mutationFn: (value: string) => step.kind === 'totp'
      ? authApi.confirmSetup(step.enrollment.pending ?? '', value)
      : Promise.reject(new Error('Настройка уже завершена')),
    onSuccess: (data) => {
      setPassword('')
      setRepeat('')
      setStep({ kind: 'codes', codes: data.recovery_codes })
    },
    onError: () => setCode(''),
  })

  if (state.isPending) return <PageSpinner />
  if (state.data && !state.data.setup_required && step.kind !== 'codes') {
    return <Navigate to={state.data.authenticated ? '/' : '/login'} replace />
  }

  const tooShort = password.length < MIN_PASSWORD
  const mismatch = repeat !== password
  const passwordError = touched && tooShort ? `Не короче ${MIN_PASSWORD} символов` : null
  const repeatError = touched && !tooShort && mismatch ? 'Пароли не совпадают' : null

  const submitPassword = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (!tooShort && !mismatch) begin.mutate()
  }

  const submitCode = (e: FormEvent) => {
    e.preventDefault()
    const value = digitsOnly(code)
    if (value.length === 6) confirm.mutate(value)
  }

  if (step.kind === 'password') {
    return (
      <AuthLayout
        title="Первичная настройка"
        description="Создай аккаунт администратора, затем подключи обязательную двухфакторную аутентификацию."
      >
        <StepMark current={1} />
        <form onSubmit={submitPassword} className="flex flex-col gap-4" noValidate>
          <Input label="Email" type="email" name="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input
            label="Пароль"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={`Не короче ${MIN_PASSWORD} символов. Лучше — фраза из нескольких слов.`}
            error={passwordError}
          />
          <Input
            label="Повтори пароль"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            error={repeatError}
          />
          <p className="text-sm text-muted">
            Часовой пояс: <span className="font-medium text-fg">{timezone ?? 'UTC'}</span> — взят из браузера,
            поменять можно в настройках.
          </p>
          <ErrorText error={begin.error} />
          <Button type="submit" variant="primary" size="lg" block loading={begin.isPending}>
            Дальше
          </Button>
        </form>
      </AuthLayout>
    )
  }

  if (step.kind === 'totp') {
    return (
      <AuthLayout
        title="Подключи 2FA"
        description="Отсканируй QR-код в приложении-аутентификаторе (Google Authenticator, Aegis, 1Password и т. п.) и введи код, который оно покажет."
        wide
      >
        <StepMark current={2} />
        <TotpQr uri={step.enrollment.totp_uri} secret={step.enrollment.totp_secret_display} />
        <form onSubmit={submitCode} className="mt-6 flex flex-col gap-4" noValidate>
          <TotpCodeInput
            label="Код из приложения"
            autoFocus
            value={code}
            onChange={(e) => {
              const value = e.target.value
              setCode(value)
              if (digitsOnly(value).length === 6 && !confirm.isPending) confirm.mutate(digitsOnly(value))
            }}
          />
          <ErrorText error={confirm.error} />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={confirm.isPending}
            disabled={digitsOnly(code).length !== 6}
          >
            Подтвердить
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setStep({ kind: 'password' })}>
            Назад к паролю
          </Button>
        </form>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Коды восстановления"
      description="Если потеряешь телефон с аутентификатором, войти можно одним из этих кодов. Они показаны один раз — сохрани их в менеджере паролей или распечатай."
      wide
    >
      <StepMark current={3} />
      <RecoveryCodesList codes={step.codes} />
      <label className="mt-5 flex min-h-11 cursor-pointer items-center gap-3 text-sm text-fg">
        <input
          type="checkbox"
          className="h-5 w-5 accent-[var(--accent)]"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
        />
        Я сохранил коды в надёжном месте
      </label>
      <Button
        variant="primary"
        size="lg"
        block
        className="mt-4"
            disabled={!saved}
        onClick={() => {
          void (async () => {
            const confirmed = await authApi.state()
            await setActiveIdentity(confirmed.user && !confirmed.user.requires_email && !confirmed.user.requires_totp_setup
              ? { id: confirmed.user.id, legacy_owner: confirmed.user.legacy_owner } : null)
            queryClient.setQueryData<AuthState>(authKeys.state, confirmed)
            navigate('/', { replace: true })
          })()
        }}
      >
        Готово
      </Button>
    </AuthLayout>
  )
}
